import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { AlertBox } from '~/app/components/common/Alert/AlertBox';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import { useAssetDetail } from '~/app/hooks/asset/useAssetDetail';
import { useAssetSummary } from '~/app/hooks/asset/useAssetSummary';
import { useDeleteAsset } from '~/app/hooks/asset/useDeleteAsset';
import { useAssetLogs } from '~/app/hooks/assets/useAssetLogs';
import { AssetService } from '~/app/services/asset/asset.service';
import { AssetDetailPageView } from './AssetDetailPageView';
import { shouldShowAssetCommands } from './AssetDetailCommandsTab/utils';
import { canViewAssetIncidents } from './assetDetailIncidentNavigation';
import {
  ASSET_DETAIL_LOCATION_SECTION_ID,
  DEFAULT_ASSET_DETAIL_TAB,
  parseAssetDetailTabParam,
  resolveAssetDetailTabKey,
} from './assetDetailTabs';

function getAssetUploadClientName(
  organizationId: string,
  rawData: Record<string, unknown> | null | undefined,
): string {
  const candidateKeys = [
    'organizationName',
    'organization_name',
    'clientName',
    'client_name',
  ];

  for (const key of candidateKeys) {
    const value = rawData?.[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return organizationId;
}

export function AssetDetailPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const params = useParams<{ assetId: string }>();
  const { t } = useTranslation('assets');
  const assetId = params.assetId ?? '';
  const canDownloadBareLogs = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const { hasPermission } = usePermission();
  const canUpdateAsset = hasPermission(PermissionKey.ASSETS_UPDATE);
  const showIncidentsTab = canViewAssetIncidents(hasPermission);

  const {
    summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useAssetSummary(user?.organizationId ?? '', assetId);

  const ocppSettingsQuery = useQuery({
    queryKey: ['assets', 'ocpp-settings', summary?.organizationId, assetId],
    queryFn: async () => {
      if (!summary?.organizationId) return null;
      return AssetService.getOcppSettings(summary.organizationId, assetId);
    },
    enabled: Boolean(assetId) && Boolean(summary?.organizationId),
  });
  const showCommandsTab = shouldShowAssetCommands({
    externalCsmsEnabled: ocppSettingsQuery.data?.externalCsmsEnabled === true,
    canUpdateAsset,
  });
  const requestedTab = parseAssetDetailTabParam(searchParams.get('tab'));
  // Confirmed unavailable only after ocpp-settings succeeds with false.
  // While loading/error, preserve deep-link and (after details load) org CSMS flag.
  const provisionalShowOcppAuthTab =
    ocppSettingsQuery.data?.externalCsmsEnabled === true ||
    (!ocppSettingsQuery.isSuccess &&
      (requestedTab === 'ocpp-auth' || requestedTab === 'ocpp-configuration'));
  const provisionalActiveTab = resolveAssetDetailTabKey({
    requested: searchParams.get('tab'),
    showIncidents: showIncidentsTab,
    showCommands: showCommandsTab,
    showOcppConfiguration: provisionalShowOcppAuthTab,
    showOcppAuth: provisionalShowOcppAuthTab,
  });
  const needsFullDetails =
    provisionalActiveTab === 'details' ||
    provisionalActiveTab === 'ocpp-auth' ||
    ocppSettingsQuery.isError;

  const {
    asset,
    assetModels,
    manufacturerOptions,
    atmmLoading,
    assetTypes,
    form,
    handleReset,
    handleSubmit,
    handleValuesChange,
    handleCoordinatesChange,
    hasChanges,
    isLoading: detailsLoading,
    isSaving,
    landlordOptions,
    siteForMap,
    sites,
    zones,
    customFields,
    customFieldsLoading,
  } = useAssetDetail(summary?.organizationId ?? '', assetId, {
    enabled: needsFullDetails,
  });

  const showOcppAuthTab =
    ocppSettingsQuery.data?.externalCsmsEnabled === true ||
    (!ocppSettingsQuery.isSuccess &&
      (asset?.organizationExternalCsmsEnabled === true ||
        requestedTab === 'ocpp-auth' ||
        requestedTab === 'ocpp-configuration'));
  const showOcppConfigurationTab = showOcppAuthTab;
  const activeTab = resolveAssetDetailTabKey({
    requested: searchParams.get('tab'),
    showIncidents: showIncidentsTab,
    showCommands: showCommandsTab,
    showOcppConfiguration: showOcppConfigurationTab,
    showOcppAuth: showOcppAuthTab,
  });

  const setActiveTab = (next: string) => {
    const resolved = resolveAssetDetailTabKey({
      requested: next,
      showIncidents: showIncidentsTab,
      showCommands: showCommandsTab,
      showOcppConfiguration: showOcppConfigurationTab,
      showOcppAuth: showOcppAuthTab,
    });

    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        if (resolved === DEFAULT_ASSET_DETAIL_TAB) {
          params.delete('tab');
        } else {
          params.set('tab', resolved);
        }
        return params;
      },
      { replace: true },
    );
  };
  const handleHeaderMapClick = () => {
    setActiveTab('details');
    requestAnimationFrame(() => {
      document
        .getElementById(ASSET_DETAIL_LOCATION_SECTION_ID)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const { confirmDelete, isDeleting, canDelete } = useDeleteAsset({
    organizationId: summary?.organizationId ?? '',
    redirectToList: true,
  });
  const assetLogs = useAssetLogs({
    organizationId: summary?.organizationId ?? '',
    assetId,
    enabled: activeTab === 'logs',
  });

  if (!assetId) {
    return (
      <ErrorBox
        title={t('detail.missingAssetId', 'Missing asset identifier')}
      />
    );
  }

  if (summaryLoading) {
    return <Loader />;
  }

  if (summaryError) {
    return (
      <ErrorBox
        title={t('detail.errorLoading', 'Failed to load asset details')}
        description={summaryError.message}
      />
    );
  }

  if (!summary) {
    return <AlertBox message={t('detail.notFound', 'No asset was found.')} />;
  }

  const mapLatitude = siteForMap?.latitude ?? summary.site.latitude ?? null;
  const mapLongitude = siteForMap?.longitude ?? summary.site.longitude ?? null;

  return (
    <AssetDetailPageView
      summary={summary}
      asset={asset}
      assetLogs={assetLogs}
      activeTab={activeTab}
      assetModels={assetModels}
      manufacturerOptions={manufacturerOptions}
      atmmLoading={atmmLoading}
      assetTypes={assetTypes}
      form={form}
      hasChanges={hasChanges}
      isSaving={isSaving}
      detailsReady={Boolean(asset) && !detailsLoading}
      onBackAction={() => navigate('/assets')}
      onActiveTabChangeAction={setActiveTab}
      onResetAction={handleReset}
      onSubmitAction={handleSubmit}
      onDeleteAction={canDelete ? confirmDelete : undefined}
      isDeleting={isDeleting}
      onValuesChangeAction={handleValuesChange}
      onCoordinatesChangeAction={handleCoordinatesChange}
      landlordOptions={landlordOptions}
      siteForMap={siteForMap}
      sites={sites}
      uploadClientName={getAssetUploadClientName(
        summary.organizationId,
        asset?.rawData,
      )}
      zones={zones}
      canDownloadBareLogs={canDownloadBareLogs}
      customFields={customFields}
      customFieldsLoading={customFieldsLoading}
      showIncidentsTab={showIncidentsTab}
      showCommandsTab={showCommandsTab}
      showOcppConfigurationTab={showOcppConfigurationTab}
      showOcppAuthTab={showOcppAuthTab}
      canUpdateAsset={canUpdateAsset}
      organizationChargerAuthEnabled={
        ocppSettingsQuery.data?.chargerAuthenticationEnabled === true
      }
      organizationOcppSettingsLoading={ocppSettingsQuery.isLoading}
      mapLatitude={mapLatitude}
      mapLongitude={mapLongitude}
      onHeaderMapClickAction={handleHeaderMapClick}
    />
  );
}
